import type { QueryClient } from "@tanstack/react-query";
import {
  SHOPPING_CART_QUERY_KEY,
  SHOPPING_CART_LINES_QUERY_KEY,
  CART_PRODUCT_AVAILABILITY_QUERY_KEY,
  CART_PRODUCT_THUMBNAILS_QUERY_KEY,
} from "@features/unified-user-header-feature/utils/HeaderQueryKeys";

/**
 * Single source of truth for cart-related React Query keys so the mini-cart
 * dropdown and the full cart page read/write the same cache entries and cannot
 * drift apart. All cart mutations should invalidate via `invalidateCart`.
 */
export const cartKeys = {
  all: [SHOPPING_CART_QUERY_KEY] as const,
  items: () => [SHOPPING_CART_QUERY_KEY] as const,
  lines: () => [SHOPPING_CART_LINES_QUERY_KEY] as const,
  availability: (itemIds: number[]) =>
    [CART_PRODUCT_AVAILABILITY_QUERY_KEY, itemIds] as const,
  thumbnails: (itemIds: number[]) =>
    [CART_PRODUCT_THUMBNAILS_QUERY_KEY, itemIds] as const,
};

/**
 * Invalidate the cart *contents* caches so the mini-cart and the full cart page stay in
 * sync: the flat mini-cart items (legacy key) and the enriched full-page lines
 * (device + warranty).
 *
 * Availability and thumbnail data remain scoped to their consuming surfaces. The full cart
 * automatically refreshes availability when its item-id query key changes, while the
 * confirmation modal checks its just-added item directly.
 */
export const invalidateCart = async (
  queryClient: QueryClient,
): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: cartKeys.all }),
    queryClient.invalidateQueries({ queryKey: cartKeys.lines() }),
  ]);
};
